import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { inr, short, date, today } from '../lib/fmt';
import { Button, Card, Note, PageHead, Pill, Tag, Tile, Tiles,
         Table, Th, Td, TableWrap, Empty } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton } from '../components/skeleton';

interface Plan {
  _id: string; name: string; amount: number; currency: string; cadence: string;
  next_run: string; ends_on?: string | null; active: boolean; po_number?: string | null;
  client?: { name: string } | null;
}
interface Upcoming {
  plan_id: string; name: string; client_name: string; amount: number; currency: string;
  raise_on: string; due_on: string; days_away: number; is_due: boolean; blocker: string | null;
}
interface Feed {
  plans: Plan[]; monthly: number; annualised: number;
  due: { plan_id: string; amount: number; currency: string }[];
  upcoming: Upcoming[];
}

/** Head, the four totals, what is coming up, then the plans themselves. */
function RetainersSkeleton() {
  return (
    <Loading label="Loading retainers">
      <HeadSkeleton actions={2} />
      <TilesSkeleton />
      <CardSkeleton flush className="mb-5"><TableSkeleton rows={3} cols={5} /></CardSkeleton>
      <CardSkeleton flush sub={false}><TableSkeleton rows={6} cols={6} /></CardSkeleton>
    </Loading>
  );
}

export default function Retainers() {
  const { can } = useSession();
  const feed = useAsync<Feed>(() => api.get('/api/recurring'), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  if (feed.loading) return <RetainersSkeleton />;
  if (feed.error) return <Note title="That did not load" tone="critical">{feed.error}</Note>;
  const d = feed.data!;
  const active = d.plans.filter((p) => p.active);
  const blocked = d.upcoming.filter((u) => u.blocker);

  async function generate() {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await api.post<{ created: { number: string }[] }>('/api/recurring/generate', {});
      setMsg(r.created.length
        ? `Raised ${r.created.length} draft${r.created.length === 1 ? '' : 's'}: ${r.created.map((c) => c.number).join(', ')}`
        : 'Nothing was due.');
      feed.reload();
    } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <PageHead eyebrow="Recurring revenue" title="Retainers"
        dek="The book that bills itself. Raising a cycle produces drafts — nothing goes out until you review and mark it sent."
        actions={can('invoice:create') && d.due.length > 0 &&
          <Button variant="primary" onClick={() => void generate()} disabled={busy}>
            {busy ? 'Raising…' : `Raise ${d.due.length} due`}
          </Button>} />

      {msg && <Note title="Done" tone="good">{msg}</Note>}
      {err && <Note title="That did not work" tone="critical">{err}</Note>}

      <Tiles>
        <Tile hero label="Contracted monthly" value={short(d.monthly)}
              sub={<span className="text-muted">{active.length} active plans</span>} />
        <Tile label="Annualised" value={short(d.annualised)}
              sub={<span className="text-muted">at today's run rate</span>} />
        <Tile label="Due to raise" value={String(d.due.length)}
              sub={<span className="text-muted">{d.due.length ? 'on or before today' : 'nothing pending'}</span>} />
        <Tile label="Next raise"
              value={active.length ? date(active.map((p) => p.next_run).sort()[0]!) : '—'}
              sub={<span className="text-muted">earliest scheduled</span>} />
      </Tiles>

      {blocked.length > 0 && (
        <Note title="These would be rejected" tone="critical">
          {blocked.map((b) => `${b.name} (${b.client_name}): ${b.blocker}`).join(' · ')}
        </Note>
      )}

      {d.upcoming.length > 0 && (
        <Card title="Coming up" sub="Worked out in advance so the accountant can verify before anything is due."
              className="mb-5" flush>
          <TableWrap>
            <Table cards>
              <thead><tr>
                <Th>Plan</Th><Th>Client</Th><Th>Raise on</Th><Th>Payment due</Th><Th n>Amount</Th><Th />
              </tr></thead>
              <tbody>
                {d.upcoming.map((u) => (
                  <tr key={u.plan_id + u.raise_on}>
                    <Td label="Plan"><b>{u.name}</b></Td>
                    <Td label="Client">{u.client_name}</Td>
                    <Td label="Raise on" className="font-mono text-[11.5px]">
                      {date(u.raise_on)}
                      {u.is_due ? <> <Tag tone="brand">due</Tag></>
                                : <span className="text-muted"> · in {u.days_away}d</span>}
                    </Td>
                    <Td label="Payment due" className="font-mono text-[11.5px] text-muted">{date(u.due_on)}</Td>
                    <Td label="Amount" n>{u.currency === 'INR' ? inr(u.amount) : `$${(u.amount / 100).toFixed(2)}`}</Td>
                    <Td label="">{u.blocker && <Tag tone="brand">needs a PO</Tag>}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      <Card title="Plans" flush>
        {!d.plans.length ? <Empty>No retainer plans yet.</Empty> : (
          <TableWrap>
            <Table cards>
              <thead><tr>
                <Th>Plan</Th><Th>Client</Th><Th>Cadence</Th><Th>Next raise</Th>
                <Th>Ends</Th><Th n>Amount</Th><Th>State</Th>
              </tr></thead>
              <tbody>
                {d.plans.map((p) => {
                  const due = p.active && p.next_run <= today();
                  return (
                    <tr key={p._id} className={p.active ? '' : 'opacity-55'}>
                      <Td label="Plan">{p.name}</Td>
                      <Td label="Client">{p.client?.name ?? '—'}</Td>
                      <Td label="Cadence"><Tag>{p.cadence}</Tag></Td>
                      <Td label="Next raise" className={`font-mono text-[11.5px] ${due ? 'font-semibold' : ''}`}>{date(p.next_run)}</Td>
                      <Td label="Ends" className="font-mono text-[11.5px] text-muted">{p.ends_on ? date(p.ends_on) : 'open'}</Td>
                      <Td label="Amount" n>{p.currency === 'INR' ? inr(p.amount) : `$${(p.amount / 100).toFixed(2)}`}</Td>
                      <Td label="State"><Pill kind={p.active ? (due ? 'approved' : 'active') : 'draft'}>
                        {p.active ? (due ? 'Due' : 'Active') : 'Paused'}</Pill></Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
